import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { FaTable, FaChair, FaCircle, FaSquare, FaExchangeAlt, FaTimes } from 'react-icons/fa';
import { GiParkBench } from 'react-icons/gi';
import { IP } from "../configuracion.js";
import { API_REVENU_LAYOUT } from "./config.js";
import "../styles/TransferTableSelector.css";
import { useProperty } from "../context/PropertyContext";

const svgComponents = {
  Table: FaTable,
  Bench: GiParkBench,
  Chair: FaChair,
  Circle: FaCircle,
  Square: FaSquare,
};

const TransferTableSelector = ({
  revenues,
  onTransferComplete,
  onClose,
  currentCheque,
  currentUser
}) => {
  const { property } = useProperty();
  const [selectedRevenue, setSelectedRevenue] = useState(null);
  const [availableSpaces, setAvailableSpaces] = useState([]);
  const [occupiedSpaces, setOccupiedSpaces] = useState([]);
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [layoutImage, setLayoutImage] = useState(null);
  const [sections, setSections] = useState([]);
  const [propertyConfig, setPropertyConfig] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);

  // ✅ Reasignación de vendedor al transferir mesa (selección manual).
  // pendingTransferSpace: mesa destino ya elegida, esperando confirmar
  // vendedor antes de ejecutar la transferencia real.
  // selectedVendorId: "keep" = mantener el vendedor actual del cheque
  // (comportamiento idéntico al de antes si no se toca nada).
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [pendingTransferSpace, setPendingTransferSpace] = useState(null);
  const [selectedVendorId, setSelectedVendorId] = useState("keep");

  useEffect(() => {
    const loadConfig = async () => {
      if (!property) return;
      try {
        const response = await fetch(`${IP}/tavpos/backend/data/${property}/config.json`);
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        const data = await response.json();
        setPropertyConfig(data);
      } catch (err) {
        console.error("Error cargando config.json:", err.message);
        setError("No se pudo cargar la configuración.");
      }
    };
    loadConfig();
  }, [property]);

  // ✅ Carga de empleados para el selector de vendedor destino (mismo
  // endpoint que ya usa Login.js). Si falla, no bloquea la transferencia:
  // simplemente el usuario solo podrá "mantener vendedor actual".
  useEffect(() => {
    const loadEmployees = async () => {
      if (!property) return;
      setEmployeesLoading(true);
      try {
        const response = await fetch(`${IP}/tavpos/backend/data/${property}/employees.json`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.empleados)
          ? data.empleados
          : [];
        setEmployees(list);
      } catch (err) {
        console.error("Error cargando empleados para reasignar vendedor:", err.message);
        setEmployees([]);
      } finally {
        setEmployeesLoading(false);
      }
    };
    loadEmployees();
  }, [property]);

  const getPropiedadId = () => {
    if (!propertyConfig || !propertyConfig.suc) {
      console.warn(`No se encontró 'suc' en config.json para la propiedad: ${property}`);
      setError("No se encontró el ID de la propiedad en la configuración");
      return null;
    }
    return Number(propertyConfig.suc);
  };

  const normalizeIconType = (iconType) => {
    if (!iconType) return '';
    const baseName = iconType.split('/').pop()?.split('.')[0] || '';
    return baseName.toLowerCase().replace(/[\s_]/g, '-');
  };

  const fetchLayout = async (tiendaId) => {
    const propiedadId = getPropiedadId();
    if (!propiedadId || !tiendaId) {
      setLayoutImage(null);
      return;
    }
    try {
      const payload = {
        propiedad_id: propiedadId,
        tienda_id: Number(tiendaId),
        type: 'layout',
        action: 'read',
      };
      const response = await fetch(API_REVENU_LAYOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') ? `Bearer ${localStorage.getItem('authToken')}` : undefined,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.status === 'success' && data.data?.layout) {
        setLayoutImage(data.data.layout);
      } else {
        setLayoutImage(null);
      }
    } catch (err) {
      console.error('Error al cargar el layout:', err.message);
      setLayoutImage(null);
    }
  };

  const fetchSections = async (tiendaId) => {
    const propiedadId = getPropiedadId();
    if (!propiedadId || !tiendaId) return [];
    try {
      const payload = {
        propiedad_id: propiedadId,
        tienda_id: Number(tiendaId),
        type: 'section',
        action: 'read',
      };
      const response = await fetch(API_REVENU_LAYOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') ? `Bearer ${localStorage.getItem('authToken')}` : undefined,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.status === 'success' && Array.isArray(data.data?.sections)) {
        setSections(data.data.sections);
        return data.data.sections;
      }
      return [];
    } catch (err) {
      console.error('Error fetching sections:', err.message);
      return [];
    }
  };

  const fetchSpaces = async (tiendaId) => {
    const propiedadId = getPropiedadId();
    if (!propiedadId || !tiendaId) return [];
    try {
      const payload = {
        propiedad_id: propiedadId,
        tienda_id: Number(tiendaId),
        type: 'espacio',
        action: 'read',
      };
      const response = await fetch(API_REVENU_LAYOUT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') ? `Bearer ${localStorage.getItem('authToken')}` : undefined,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.status === 'success' && data.data?.espaciosBySection) {
        const spaces = Object.values(data.data.espaciosBySection).flat().map(space => ({
          id: space.id,
          name: space.name,
          section: space.Seccion || 'Sin sección',
          x: space.ejex,
          y: space.ejey,
          type: space.icon_type || space.img || '',
          capacity: space.cantidad,
        }));
        return spaces;
      }
      return [];
    } catch (err) {
      console.error('Error fetching spaces:', err.message);
      return [];
    }
  };

  const fetchSpaceStatus = async () => {
    if (!property) {
      setError("No se proporcionó un valor válido para 'property'");
      return [];
    }

    setLoading(true);
    setError(null);

    try {
      const url = `${IP}/tavpos/backend/lectura_cheques.php?property=${encodeURIComponent(property)}`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const text = await response.text();
      let data;

      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error("Error parsing JSON:", parseError);
        console.log("Raw response:", text);
        throw new Error("Invalid JSON response");
      }

      if (!data.success) {
        throw new Error(data.message || "Error en la respuesta del servidor");
      }

      const chequesData = Array.isArray(data.data) ? data.data : [];
      setCheques(chequesData);
      return chequesData;
    } catch (err) {
      console.error("Error fetching cheques:", err.message);
      setError(`Error al cargar mesas ocupadas: ${err.message}`);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const identifyOccupiedSpaces = (spaces, chequesData, revenueName) => {
    return spaces.map((space) => {
      const cheque = chequesData.find(
        (c) =>
          c.mesa.toLowerCase() === space.name.toLowerCase() &&
          c.revenue.toLowerCase() === revenueName.toLowerCase() &&
          c.fecha_cierre === null
      );
      return {
        ...space,
        occupied: !!cheque,
        clientCount: cheque?.cliente || 0,
        chequeData: cheque
      };
    });
  };

  const groupSpacesBySection = (spaces) => {
    return spaces.reduce((acc, space) => {
      const section = space.section || 'Sin sección';
      if (!acc[section]) acc[section] = [];
      acc[section].push(space);
      return acc;
    }, {});
  };

  const handleSelectRevenue = async (revenue) => {
    if (!revenue || !revenue.id || !revenue.name) {
      setError("Centro de consumo inválido: ID o nombre faltante");
      return;
    }

    setSelectedRevenue(revenue);

    try {
      setLoading(true);
      const [chequesData, sectionsData, spacesData] = await Promise.all([
        fetchSpaceStatus(),
        fetchSections(revenue.id),
        fetchSpaces(revenue.id),
      ]);
      await fetchLayout(revenue.id);

      const spacesWithStatus = identifyOccupiedSpaces(spacesData, chequesData, revenue.name);

      // Filtrar solo mesas disponibles (excluyendo la mesa actual)
      const availableSpacesFiltered = spacesWithStatus.filter(space =>
        !space.occupied && space.name !== currentCheque?.mesa
      );

      setAvailableSpaces(availableSpacesFiltered);
      setOccupiedSpaces(spacesWithStatus.filter((space) => space.occupied));
      setActiveSection(sectionsData[0] || 'Sin sección');

    } catch (err) {
      console.error('Error al cargar datos:', err.message);
      setError(`Error al cargar datos: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Ya no transfiere de inmediato al tocar una mesa: guarda la mesa
  // elegida y pasa al paso de selección de vendedor destino.
  const handleSelectDestinationSpace = (space) => {
    setPendingTransferSpace(space);
    setSelectedVendorId("keep");
  };

  const handleTransferToSpace = async (space, vendorOverride) => {
    if (!currentCheque || !selectedRevenue?.id) {
      setError("Datos incompletos para transferencia");
      return;
    }

    setTransferLoading(true);
    try {
      const payload = {
        action: "transfer_table",
        numero_cheque: currentCheque.numero_cheque,
        revenue_id: selectedRevenue.id,
        mesa_actual: currentCheque.mesa,
        mesa_nueva: space.name,
        // ✅ Antes iba hardcodeado como "usuario_actual". Ahora se envía el
        // usuario real de la sesión (mismo dato que ya usa el resto del
        // sistema, currentUser.nombre).
        usuario: currentUser?.nombre || "Sistema",
      };

      // ✅ Solo se envía si el usuario eligió explícitamente reasignar a
      // otro vendedor. Si no, el backend mantiene el vendedor actual del
      // cheque sin cambios (comportamiento idéntico al de antes).
      if (vendorOverride) {
        payload.vendedor_destino_id = vendorOverride.id ?? null;
        payload.vendedor_destino_nombre = vendorOverride.nombre ?? "";
      }

      const response = await fetch(
        `${IP}/tavpos/backend/transferir_mesa.php?property=${encodeURIComponent(property)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      if (!data.success) {
        throw new Error(data.message || "Error al transferir la mesa");
      }

      setPendingTransferSpace(null);

      if (onTransferComplete) {
        onTransferComplete({
          success: true,
          message: `Mesa transferida exitosamente a ${space.name}`,
          data: data.data
        });
      }

      if (onClose) {
        onClose();
      }

    } catch (err) {
      console.error("[handleTransferToSpace] Error:", err);
      setError(`⚠️ Error al transferir mesa: ${err.message}`);
    } finally {
      setTransferLoading(false);
    }
  };

  const handleConfirmTransfer = () => {
    if (!pendingTransferSpace) return;
    const vendorOverride =
      selectedVendorId !== "keep"
        ? employees.find((emp) => String(emp.id) === String(selectedVendorId))
        : null;
    handleTransferToSpace(pendingTransferSpace, vendorOverride);
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
  };

  const renderSpaces = () => {
    if (!selectedRevenue || !activeSection) return null;
    const grouped = groupSpacesBySection(availableSpaces);
    const spaces = grouped[activeSection] || [];

    return spaces.map((space) => {
      const normalizedIconType = normalizeIconType(space.type);
      const IconComponent = svgComponents[normalizedIconType] || FaTable;

      return (
        <div
          key={space.id}
          className="space-item available"
          onClick={() => !transferLoading && handleSelectDestinationSpace(space)}
          style={{
            left: `${space.x}px`,
            top: `${space.y}px`,
          }}
        >
          {IconComponent && <IconComponent size={24} />}
          <span>{space.name}</span>
          <div className="transfer-indicator">
            <FaExchangeAlt size={12} />
          </div>
        </div>
      );
    });
  };

  const renderOccupiedSpaces = () => {
    if (!selectedRevenue || !activeSection) return null;
    const grouped = groupSpacesBySection(occupiedSpaces);
    const spaces = grouped[activeSection] || [];

    return spaces.map((space) => {
      const normalizedIconType = normalizeIconType(space.type);
      const IconComponent = svgComponents[normalizedIconType] || FaTable;

      return (
        <div
          key={space.id}
          className="space-item occupied"
          style={{
            left: `${space.x}px`,
            top: `${space.y}px`,
          }}
          title={`Mesa ocupada - Cheque #${space.chequeData?.numero_cheque}`}
        >
          {IconComponent && <IconComponent size={24} />}
          <span>{space.name}</span>
          {space.clientCount > 0 && (
            <div className="client-count-badge">
              {space.clientCount}
            </div>
          )}
        </div>
      );
    });
  };

  // Solo revenues de tipo Table
  const tableRevenues = revenues.filter(r => r.Tipo === "Table" || r.Tipo === "table");

  return (
    <div className="transfer-table-selector">
      <div className="transfer-header">
        <h2><i className="bi bi-arrow-left-right" style={{marginRight:8}}></i>Transferir Mesa</h2>
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
      </div>

      {currentCheque && (
        <div className="current-cheque-info">
          <span><strong>Mesa:</strong> {currentCheque.mesa}</span>
          <span><strong>Cheque #:</strong> {currentCheque.numero_cheque}</span>
          <span><strong>Área:</strong> {currentCheque.revenue}</span>
        </div>
      )}

      {loading && <div className="loading">Cargando diagrama de mesas...</div>}
      {error && <div className={`error ${error.includes('✅') ? 'success' : ''}`}>{error}</div>}
      {transferLoading && <div className="transfer-loading">Procesando transferencia...</div>}

      {!selectedRevenue ? (
        <div className="revenue-selection">
          <h3>Selecciona un área destino</h3>
          <div className="revenue-list">
            {tableRevenues.length === 0 ? (
              <p className="no-results">No hay áreas de tipo mesa disponibles.</p>
            ) : (
              tableRevenues.map((revenue) => (
                <button
                  key={revenue.id}
                  onClick={() => handleSelectRevenue(revenue)}
                  className="revenue-button"
                >
                  {revenue.name}
                </button>
              ))
            )}
          </div>
        </div>
      ) : pendingTransferSpace ? (
        <div className="vendor-selection">
          <h3>Vendedor para la mesa {pendingTransferSpace.name}</h3>
          <p className="transfer-instruction">
            Puedes mantener al vendedor actual del cheque o reasignarlo a otro empleado.
          </p>

          <div className="revenue-list vendor-list">
            <button
              onClick={() => setSelectedVendorId("keep")}
              className={`revenue-button${selectedVendorId === "keep" ? " selected" : ""}`}
            >
              Mantener vendedor actual
            </button>
            {employeesLoading && (
              <p className="no-results">Cargando empleados...</p>
            )}
            {!employeesLoading &&
              employees.map((emp) => (
                <button
                  key={emp.id ?? emp.usr ?? emp.nombre}
                  onClick={() => setSelectedVendorId(emp.id)}
                  className={`revenue-button${String(selectedVendorId) === String(emp.id) ? " selected" : ""}`}
                >
                  {emp.nombre}
                </button>
              ))}
            {!employeesLoading && employees.length === 0 && (
              <p className="no-results">
                No se pudo cargar la lista de empleados. Puedes continuar manteniendo el vendedor actual.
              </p>
            )}
          </div>

          <div className="action-buttons">
            <button
              className="back-button"
              onClick={() => setPendingTransferSpace(null)}
              disabled={transferLoading}
            >
              ← Volver a mesas
            </button>
            <button
              className="cancel-button"
              onClick={onClose}
              disabled={transferLoading}
            >
              Cancelar
            </button>
            <button
              className="confirm-transfer-button"
              onClick={handleConfirmTransfer}
              disabled={transferLoading}
            >
              {transferLoading ? "Transfiriendo..." : "Confirmar Transferencia"}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-selection">
          <h3>Selecciona Mesa Destino en {selectedRevenue.name}</h3>
          <p className="transfer-instruction">Haz clic en una mesa disponible para transferir</p>

          <div className="section-tabs">
            {sections.length > 0 ? (
              sections.map((section) => (
                <button
                  key={section}
                  onClick={() => handleSectionChange(section)}
                  className={activeSection === section ? 'active' : ''}
                >
                  {section}
                </button>
              ))
            ) : (
              <button
                onClick={() => handleSectionChange('Sin sección')}
                className={activeSection === 'Sin sección' ? 'active' : ''}
              >
                Sin sección
              </button>
            )}
          </div>

          <div className="layout-container">
            {layoutImage ? (
              <div className="layout-image-container">
                <img src={layoutImage} alt="Layout" className="layout-image" onError={() => setLayoutImage(null)} />
                <div className="spaces-overlay">
                  {renderOccupiedSpaces()}
                  {renderSpaces()}
                </div>
              </div>
            ) : (
              <div className="layout-placeholder">
                <div className="spaces-grid">
                  {renderOccupiedSpaces()}
                  {renderSpaces()}
                </div>
              </div>
            )}
          </div>

          <div className="legend">
            <div className="legend-item">
              <div className="legend-color available"></div>
              <span>Mesas Disponibles</span>
            </div>
            <div className="legend-item">
              <div className="legend-color occupied"></div>
              <span>Mesas Ocupadas</span>
            </div>
          </div>

          {availableSpaces.length === 0 && !loading && (
            <div className="no-available-spaces">
              <p>No hay mesas disponibles en {selectedRevenue.name}</p>
            </div>
          )}

          <div className="action-buttons">
            <button
              className="back-button"
              onClick={() => setSelectedRevenue(null)}
              disabled={transferLoading}
            >
              ← Volver
            </button>
            <button
              className="cancel-button"
              onClick={onClose}
              disabled={transferLoading}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

TransferTableSelector.propTypes = {
  revenues: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      Tipo: PropTypes.string.isRequired,
    })
  ).isRequired,
  onTransferComplete: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  currentCheque: PropTypes.object.isRequired,
  currentUser: PropTypes.object,
};

export default TransferTableSelector;
